/**
 * כרטיסים — "כמה יירד מכל כרטיס, מתי, כמה מסגרת נשארה, ואם יש לזה כיסוי" (spec §4.4).
 *
 * The hero is the combined coverage view (§6.5 timeline + the known/expected figures), then a
 * panel per active card (cycle, utilisation meter §6.7, installments, last charge), the
 * 6-month schedule (§6.6) and the collapsed "כרטיסים שנסגרו" group (BRIEF §9.2).
 */

import type { CardSummary, CardsResponse, CreditStatus, SummaryResponse } from '../../../shared-types.ts';
import { fmtApprox, fmtDM, fmtDate, fmtMoney, fmtMonthNum, fmtPctPlain, monthName, monthShort } from '../format.ts';
import { CREDIT_STATUS, he, daysWord, providerName } from '../i18n/he.ts';
import { Icon } from '../components/Icon.tsx';
import { Chip, EmptyState, Ltr, Meter, Money, ProviderLogo, Section, Stat } from '../components/ui.tsx';
import { CardVisual } from '../../../../shared/ui/src/index.tsx';
import type { CardState } from '../../../../shared/ui/src/index.tsx';
import { RecentRow } from '../components/tx.tsx';
import { StackedColumns } from '../charts/StackedColumns.tsx';
import { Timeline30 } from '../charts/Timeline30.tsx';
import { useApp } from '../hooks/useApp.tsx';
import { useQuery } from '../hooks/useQuery.ts';
import { cacheKey } from '../hooks/cache.ts';
import { Link, useRouter } from '../hooks/useRouter.tsx';
import { buildPath } from '../router.ts';

/** REDESIGN §7c — `creditStatus` decides the overlay drawn across the illustrated card. */
function cardState(status: CreditStatus): CardState {
  return status === 'blocked' ? 'blocked' : status === 'deleted' ? 'closed' : 'active';
}

export function CardsScreen({ summary }: { summary: SummaryResponse | undefined }) {
  const { api, appearance } = useApp();
  const { route } = useRouter();
  const cards = useQuery<CardsResponse>(cacheKey('cards'), () => api.cards(), { scopes: ['cards', 'summary'] });

  if (!cards.data) {
    return (
      <div className="screen">
        <div className="sk" style={{ height: '220px' }} />
        <div className="sk" style={{ height: '320px' }} />
      </div>
    );
  }

  const data = cards.data;
  const decimals = appearance.exactNumbers ? 2 : 0;
  const active = data.items.filter((card) => card.creditStatus === 'enabled');
  const closed = data.items.filter((card) => card.creditStatus !== 'enabled');
  const selected = route.param ?? active[0]?.account.id ?? '';

  return (
    <div className="screen">
      <div className="g12">
        <CoverageHero data={data} summary={summary} decimals={decimals} />
        {active.length ? (
          <div className="cards2" style={{ ['--c' as string]: '12' } as Record<string, string>}>
            {active.map((card) => (
              <CardPanel key={card.account.id} card={card} decimals={decimals} open={card.account.id === selected} />
            ))}
          </div>
        ) : null}
        {data.schedule.months.length ? (
          <Section span={12}>
            <StackedColumns
              question={he.cards.scheduleQ}
              period={he.cards.schedulePeriod(
                monthName(data.schedule.months[0]?.month ?? ''),
                monthName(data.schedule.months[data.schedule.months.length - 1]?.month ?? ''),
                fmtMoney(data.schedule.totals.totalAgorot),
                fmtMoney(data.schedule.totals.knownAgorot, 2),
              )}
              summary={`${he.cards.scheduleTotal6} ≈ ${fmtMoney(data.schedule.totals.totalAgorot)}`}
              series={[
                { key: 'known', label: he.cards.scheduleKnown, series: 1 },
                { key: 'forecast', label: he.cards.scheduleForecast, series: 1, hatch: true, dim: true },
              ]}
              rows={data.schedule.months.map((month) => ({
                key: month.month,
                label: monthShort(month.month),
                aria: monthName(month.month),
                values: { known: month.knownAgorot, forecast: month.forecastAgorot },
                tip: {
                  title: `${monthName(month.month)} · ${fmtApprox(month.totalAgorot)}`,
                  rows: [
                    { color: 'var(--series-1)', value: fmtMoney(month.knownAgorot, 2), label: he.cards.scheduleKnown },
                    { color: 'var(--series-1)', value: fmtApprox(month.forecastAgorot), label: he.cards.scheduleForecast },
                  ],
                },
              }))}
              step={400000}
              height={240}
              approxTotals
              table={{
                head: [he.cats.tblMonth, he.cards.scheduleKnown, he.cards.scheduleForecast, he.cats.tblTotal],
                rows: [
                  ...data.schedule.months.map((month) => [
                    monthName(month.month),
                    fmtMoney(month.knownAgorot, 2),
                    fmtApprox(month.forecastAgorot),
                    fmtApprox(month.totalAgorot),
                  ]),
                  [
                    <b key="t">{he.cards.scheduleTotal6}</b>,
                    <b key="k">{fmtMoney(data.schedule.totals.knownAgorot, 2)}</b>,
                    <b key="f">{fmtApprox(data.schedule.totals.forecastAgorot)}</b>,
                    <b key="s">{fmtApprox(data.schedule.totals.totalAgorot)}</b>,
                  ],
                ],
              }}
            />
          </Section>
        ) : null}
        {active.map((card) => (
          <RecentCharges key={`recent-${card.account.id}`} card={card} />
        ))}
        {closed.length ? <ClosedCards cards={closed} /> : null}
      </div>
    </div>
  );
}

function CoverageHero({ data, summary, decimals }: { data: CardsResponse; summary: SummaryResponse | undefined; decimals: number }) {
  const { coverage } = data;
  const overall = coverage.overall;
  const ok = (overall?.coverageKnownAgorot ?? 0) >= 0;
  const inWindow = coverage.items.filter((item) => item.inWindow);
  const expired = (summary?.connections ?? []).filter((connection) => connection.needsRelink);
  const checking = coverage.accounts[0];

  return (
    <Section
      span={12}
      variant="hero"
      titleId="h-cov"
      title={he.cards.coverageQ}
      sub={he.cards.coverageSub(fmtDM(coverage.window.from), fmtDM(coverage.window.to))}
    >
      {!coverage.computable || !coverage.items.length ? (
        <EmptyState
          icon="card"
          title={expired.length ? he.cards.cannotCompute : he.cards.noCards}
          body={expired.length ? he.cards.cannotComputeBody : undefined}
          action={
            <Link className="btn sm" to={buildPath('settings', 'connections')}>
              {he.refresh.toConnections}
            </Link>
          }
        />
      ) : (
        <>
          <div className="cov-grid">
            <Stat
              label={he.home.available(checking?.numberLast4 ? `***${checking.numberLast4}` : '')}
              size="big"
              value={<Money agorot={overall?.availableAgorot ?? 0} decimals={decimals} />}
              delta={checking?.bookedInclLimitAgorot ? he.nw.overdraft(fmtMoney(checking.bookedInclLimitAgorot)) : undefined}
            />
            <div className="cov-mid">
              <Timeline30
                asOf={data.asOf}
                big
                items={inWindow.map((item) => ({
                  date: item.dueDate,
                  amount: item.knownAgorot,
                  label: item.name,
                  final: item.cycle.status === 'final',
                  expired: item.connectionStatus !== 'ACTIVE',
                  aria: `${item.name} ${item.numberLast4 ?? ''} · ${fmtDate(item.dueDate)} · ${fmtMoney(item.knownAgorot)}`,
                  tip: {
                    title: `${item.name} ${item.numberLast4 ? `****${item.numberLast4}` : ''} · ${fmtDate(item.dueDate)}`,
                    rows: [
                      { value: fmtMoney(item.knownAgorot), label: item.cycle.status === 'final' ? he.home.final : he.home.accrued },
                      { label: `מחזור ${fmtDM(item.cycle.start)}–${fmtDM(item.cycle.end)}` },
                    ],
                  },
                }))}
              />
              <div className="timeline-list hairlist">
                {coverage.items.slice(0, 3).map((item) => (
                  <div className="listrow" style={{ minHeight: '40px', opacity: item.inWindow ? 1 : 0.65 }} key={`${item.accountId}-${item.dueDate}`}>
                    <ProviderLogo providerId={item.providerId} title={providerName(item.providerId)} />
                    <span className="main">
                      <span className="name">
                        <Ltr>{`${item.name} ${item.numberLast4 ? `****${item.numberLast4}` : ''}`}</Ltr>
                      </span>
                      <span className="sub">
                        <span className="num">{fmtDate(item.dueDate)}</span> · {daysWord(item.daysUntil)}
                      </span>
                    </span>
                    <span className="amt">
                      <span className="num">{fmtMoney(item.knownAgorot, decimals)}</span>
                      <br />
                      {item.cycle.status === 'final' ? <Chip tone="info" icon="lock">{he.home.final}</Chip> : <Chip tone="muted" icon="clock">{he.home.accruedShort}</Chip>}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="stack" style={{ gap: '10px' }}>
              <Stat label={he.home.charges30} value={<Money agorot={overall?.chargesKnownAgorot ?? 0} decimals={decimals} />} />
              <Stat
                label={he.home.leftAfter}
                tone={ok ? 'good' : 'critical'}
                value={
                  <>
                    <Money agorot={overall?.coverageKnownAgorot ?? 0} decimals={decimals} />
                    <Icon name={ok ? 'checkCircle' : 'alert'} className="s" />
                  </>
                }
                delta={ok ? he.home.covered : he.cards.missingHint(fmtMoney(Math.abs(overall?.coverageKnownAgorot ?? 0)))}
              />
            </div>
          </div>
          <div className="meta">
            {he.home.knownAmounts} (
            {inWindow.map((item) => `${item.name} ${item.cycle.status === 'final' ? he.home.final : he.home.accruedShort} ${fmtMoney(item.knownAgorot)}`).join(' · ')}
            ).
            {inWindow
              .filter((item) => item.cycle.status === 'open')
              .map(
                (item) =>
                  ` ${he.home.coverageNoteOpen(item.name, fmtDM(item.cycle.end), fmtMoney(item.expectedAgorot))} ${
                    (overall?.coverageExpectedAgorot ?? 0) >= 0
                      ? he.home.coverageStillOk(fmtMoney(overall?.coverageExpectedAgorot ?? 0))
                      : he.home.coverageWillMiss(fmtMoney(Math.abs(overall?.coverageExpectedAgorot ?? 0)))
                  }.`,
              )
              .join('')}{' '}
            {he.home.coverageExclusions}.
          </div>
          {coverage.excluded.map((row) => (
            <div className="meta" key={row.accountId}>
              {he.cards.chargedFromUnassigned(row.chargedFromLast4 ? `***${row.chargedFromLast4}` : '')}
            </div>
          ))}
          {expired.length ? (
            <div className="notice warn">
              <Icon name="alert" />
              <span>{he.cards.expiredDataNote(expired.map((c) => providerName(c.providerId)).join(', '), fmtDM(data.asOf))}</span>
            </div>
          ) : null}
        </>
      )}
    </Section>
  );
}

function CardPanel({ card, decimals, open }: { card: CardSummary; decimals: number; open: boolean }) {
  const status = CREDIT_STATUS[card.creditStatus];
  const expired = card.account.connectionStatus !== 'ACTIVE';
  const settings = card.settings;
  const label = `${card.account.name} ${card.account.numberLast4 ? `****${card.account.numberLast4}` : ''}`;
  const utilization = card.utilization ?? 0;
  const dueSource =
    settings.dueDaySource === 'financy' ? he.cards.dueDayFinancy : settings.dueDaySource === 'manual' ? he.cards.dueDayManual : he.cards.dueDayDerived;

  return (
    <details className="acc card-panel" id={`card-${card.account.id}`} open={open} style={{ border: 0 }}>
      <summary className="only-m-summary">
        <span className="row wrap" style={{ gap: '10px' }}>
          <ProviderLogo providerId={card.account.providerId} size="lg" title={providerName(card.account.providerId)} />
          <Ltr>{label}</Ltr>
          <Chip tone={status.chip as 'good'} icon={status.icon}>
            {status.label}
          </Chip>
        </span>
        <Icon name="chevD" />
      </summary>
      <section className={`sec panel${expired ? ' t3' : ''}`} style={expired ? { opacity: 0.85 } : undefined} aria-labelledby={`h-card-${card.account.id}`}>
        <div className="sec-h only-d">
          <h2 id={`h-card-${card.account.id}`} className="row" style={{ gap: '10px' }}>
            <ProviderLogo providerId={card.account.providerId} size="lg" title={providerName(card.account.providerId)} />
            <Ltr>{label}</Ltr>
            <Chip tone={status.chip as 'good'} icon={status.icon}>
              {status.label}
            </Chip>
            {expired ? (
              <Chip tone="critical" icon="alert">
                {he.cards.connectionExpiredChip}
              </Chip>
            ) : null}
          </h2>
        </div>
        <div className="meta">
          {card.product ? `${card.product} · ` : ''}
          {he.cards.dueDayLine(settings.dueDay, settings.cutoffDay, dueSource)}
          {card.dueDayUnverified ? ` · ${he.cards.dueDayUnverified}` : ''}
        </div>
        {expired && card.account.dataAsOf ? (
          <div className="notice warn">
            <Icon name="alert" />
            <span>{he.cards.expiredNotice(fmtDate(card.account.dataAsOf.slice(0, 10)))}</span>
          </div>
        ) : null}

        <div className="cardvis-row">
          <CardVisual
            providerId={card.account.providerId}
            last4={card.account.numberLast4}
            state={cardState(card.creditStatus)}
            stateLabel={status.label}
            size="md"
            label={card.settings.label ?? card.product ?? undefined}
            title={`${card.account.name} ${card.account.numberLast4 ? `****${card.account.numberLast4}` : ''}`}
          />
        </div>

        {card.final ? (
          <div
            className="listrow"
            style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '8px 12px', background: 'var(--surface-2)' }}
          >
            <span className="main">
              <span className="name">{he.cards.cycle(fmtDM(card.final.start), fmtDate(card.final.end))}</span>
              <span className="sub">
                {he.cards.willCharge('')}
                <b className="num">{fmtDate(card.final.dueDate)}</b>
              </span>
            </span>
            <span className="amt">
              <span className="num">{fmtMoney(card.final.amountAgorot, decimals)}</span>
              <br />
              <Chip tone="info" icon="lock">
                {he.home.final}
              </Chip>
            </span>
          </div>
        ) : null}

        {card.current ? (
          <div className="stat">
            <span className="l">
              {he.cards.cycle(fmtDM(card.current.start), fmtDate(card.current.end))} · {he.cards.willCharge('')}
              <span className="num">{fmtDate(card.current.dueDate)}</span>
            </span>
            <span className="v">
              <Money agorot={card.current.amountAgorot} decimals={decimals} />{' '}
              <Chip tone="muted" icon="clock">
                {he.home.accrued}
              </Chip>
            </span>
            <span className="d">
              {he.cards.expectedEnd(fmtApprox(card.current.expectedAgorot))}{' '}
              <span className="meta">
                {he.cards.expectedParts(fmtApprox(card.variableForecastAgorot), fmtMoney(card.current.futureInstallmentAgorot, 2))}
              </span>
            </span>
          </div>
        ) : null}

        <div className="stack" style={{ gap: '6px' }}>
          <div className="row between small">
            <span>{he.cards.utilization}</span>
            <span>
              {he.cards.utilizationOf(fmtMoney(card.usedAgorot, decimals), fmtMoney(card.creditLimitAgorot), Math.round(utilization * 100))}
            </span>
          </div>
          <Meter ratio={utilization} label={he.cards.utilizationAria(Math.round(utilization * 100))} />
          <div className="meta row between">
            <span>
              {utilization > 0.9 ? (
                <span className="critical">
                  <Icon name="alert" className="xs" /> {he.cards.utilizationNear}
                </span>
              ) : utilization > 0.7 ? (
                <span className="warn">
                  <Icon name="alert" className="xs" /> {he.cards.utilizationHigh}
                </span>
              ) : (
                he.cards.utilizationLeft(fmtMoney((card.creditLimitAgorot ?? 0) - card.usedAgorot))
              )}
            </span>
            <span>{fmtPctPlain(utilization)}</span>
          </div>
        </div>

        {card.installmentsAvailable ? (
          card.installments.plans ? (
            <div className="meta">
              {he.cards.installmentsSummary(
                card.installments.plans,
                fmtMoney(card.installments.monthlyAgorot, 2),
                fmtMonthNum(card.installments.lastDueMonth),
              )}
            </div>
          ) : null
        ) : (
          <div className="meta">{he.cards.installmentsUnavailable}</div>
        )}

        {card.lastCharged ? (
          <div className="meta">
            {he.cards.lastCharge(fmtDate(card.lastCharged.dueDate), fmtMoney(card.lastCharged.amountAgorot, decimals))}
            {card.lastCharged.matchedChargeTxId ? (
              <>
                {' · '}
                <span className="good">
                  {he.cards.bookedInBank} <Icon name="check" className="xs" />
                </span>
              </>
            ) : null}
          </div>
        ) : null}

        <div>
          <Link className="link" to={buildPath('cards', card.account.id)}>
            {he.app.details}
            <Icon name="chevL" className="xs" />
          </Link>
        </div>
      </section>
    </details>
  );
}

function RecentCharges({ card }: { card: CardSummary }) {
  const { api } = useApp();
  const detail = useQuery(cacheKey('card', { id: card.account.id }), () => api.card(card.account.id), { scopes: ['cards'] });
  const rows = detail.data?.recent ?? [];
  return (
    <Section
      span={6}
      titleId={`h-rc-${card.account.id}`}
      title={he.cards.recentTitle(`${card.account.name} ${card.account.numberLast4 ? `****${card.account.numberLast4}` : ''}`)}
      action={
        <Link className="link" to={buildPath('transactions', null, { account: card.account.id })}>
          {he.cards.allOfCard(card.account.name)}
          <Icon name="chevL" className="xs" />
        </Link>
      }
    >
      <div className="hairlist">
        {rows.length ? rows.map((row) => <RecentRow key={row.id} row={row} />) : <EmptyState title={he.cards.noCharges} />}
      </div>
    </Section>
  );
}

function ClosedCards({ cards }: { cards: CardSummary[] }) {
  const blocked = cards.filter((card) => card.creditStatus === 'blocked');
  const deleted = cards.filter((card) => card.creditStatus === 'deleted');
  return (
    <Section span={12} className="closed-cards">
      <details className="acc" style={{ border: 0 }}>
        <summary className="acc-head">
          <h2 style={{ fontSize: '16px' }}>
            {he.cards.closedTitle} · <span className="num">{cards.length}</span>
          </h2>
          <Chip tone="warn" icon="lock">
            {he.cards.closedBlocked(blocked.length)}
          </Chip>
          <Chip tone="muted">{he.cards.closedDeleted(deleted.length)}</Chip>
          <Icon name="chevD" />
        </summary>
        <p className="meta" style={{ paddingTop: '6px' }}>
          {he.cards.closedNote}
        </p>
        <div className="hairlist">
          {[...blocked, ...deleted].map((card) => {
            const status = CREDIT_STATUS[card.creditStatus];
            return (
              <div className="listrow" key={card.account.id}>
                <CardVisual
                  providerId={card.account.providerId}
                  last4={card.account.numberLast4}
                  state={cardState(card.creditStatus)}
                  stateLabel={status.label}
                  size="sm"
                  title={`${card.account.name} ${card.account.numberLast4 ? `****${card.account.numberLast4}` : ''}`}
                />
                <span className="main">
                  <span className="name">
                    <Ltr>{`${card.account.name} ${card.account.numberLast4 ? `****${card.account.numberLast4}` : ''}`}</Ltr>{' '}
                    <Chip tone={status.chip as 'muted'} icon={status.icon}>
                      {status.label}
                    </Chip>
                  </span>
                  <span className="sub">
                    {card.product ? `${card.product} · ` : ''}
                    {card.settings.label ?? ''}
                    {card.creditLimitAgorot ? ` · ${he.cards.limit} ${fmtMoney(card.creditLimitAgorot)}` : ''}
                  </span>
                </span>
                <span className="amt" style={{ fontWeight: 500 }}>
                  <Link className="link" to={buildPath('transactions', null, { account: card.account.id, period: '12m' })}>
                    {he.cards.history}
                    {card.txCount ? ` (${card.txCount})` : ''}
                    <Icon name="chevL" className="xs" />
                  </Link>
                </span>
              </div>
            );
          })}
        </div>
      </details>
    </Section>
  );
}
